import React, { useEffect, useRef, useState } from "react";
import { X, Upload, CheckCircle2, AlertCircle, FileSpreadsheet, Zap, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Three physical meters commonly present on a site — every CSV upload
// must be tagged with exactly one of these so the backend knows which
// canonical energy slot ({generation,import,export}_kwh) to fill.
const METERS = [
  { key: "generation", label: "Generation",   icon: Zap,
    hint: "PV inverter / production meter — populates generation_kwh." },
  { key: "import",     label: "Import",       icon: ArrowDownToLine,
    hint: "Grid-in meter — populates import_kwh." },
  { key: "export",     label: "Export",       icon: ArrowUpFromLine,
    hint: "Grid-out / spill meter — populates export_kwh." },
];
const METER_LABEL = Object.fromEntries(METERS.map((m) => [m.key, m.label]));

/**
 * 3-step CSV import wizard for the 15-min monitoring pipeline.
 *   Step 1 · Upload — drag-drop a CSV; backend parses + autodetects columns.
 *   Step 2 · Map — user confirms/remaps the 5 canonical fields
 *                  (timestamp / generation / consumption / export / import).
 *   Step 3 · Commit — POST to the commit endpoint; show inserted vs skipped.
 *
 * Insert-if-absent semantics on the backend: CSV rows only fill missing
 * timestamps; existing SolarEdge / MeterOnline data is untouched.
 */
const FIELDS = [
  { key: "timestamp",       label: "Timestamp",       required: false,
    hint: "Combined date + time (ISO 8601, dd/mm/yyyy hh:mm, etc). Leave blank if you have separate date + time columns below." },
  { key: "date_col",        label: "Start Date",      required: false,
    hint: "Date-only column (e.g. 01/06/2024). Pair with the 'Reading Time' column below if your CSV splits date and time." },
  { key: "time_col",        label: "Reading Time",    required: false,
    hint: "Time-of-day column (e.g. 14:30 or 2:30 PM). Combined with Start Date to form the interval timestamp." },
  { key: "generation_kwh",  label: "Generation kWh",  required: false,
    hint: "PV output during this period. Optional — but at least one energy column is required." },
  { key: "consumption_kwh", label: "Total Consumption kWh", required: false,
    hint: "Total site load (BTM + import). Optional." },
  { key: "self_consumption_kwh", label: "Total Self Consumption kWh", required: false,
    hint: "PV kWh consumed on-site (not exported). When provided, wins over the derived value." },
  { key: "export_kwh",      label: "Export kWh",      required: false,
    hint: "kWh spilled to grid. Optional." },
  { key: "import_kwh",      label: "Import kWh",      required: false,
    hint: "kWh drawn from grid. Optional." },
];

export default function ImportCsvDialog({ projectId, onClose, onImported }) {
  const [step, setStep] = useState(1);            // 1 upload · 2 map · 3 done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [meter, setMeter] = useState("");         // "generation" | "import" | "export"
  const [meterId, setMeterId] = useState("");     // specific meter row id (required when 2+ exist for the type)
  const [projectMeters, setProjectMeters] = useState([]);
  const [skipRows, setSkipRows] = useState(0);    // preamble/title rows to discard before header
  const [headerAutoDetected, setHeaderAutoDetected] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [wipeRange, setWipeRange] = useState(false);
  const fileRef = useRef(null);

  // Load registered meters once — used to require a specific target when
  // the project has 2+ meters of the chosen type.
  useEffect(() => {
    api.get(`/projects/${projectId}/meters`)
      .then(r => setProjectMeters((r.data || []).filter(m => m.active)))
      .catch(() => setProjectMeters([]));
  }, [projectId]);

  // skip param semantics: null → let backend auto-detect (-1);
  // number → explicit override.
  const runPreview = async (f, skip) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("skip_rows", String(skip === null || skip === undefined ? -1 : skip));
      const { data } = await api.post(
        `/monitoring/${projectId}/import-csv/preview`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      // Backend echoes back the effective offset (either what the user sent,
      // or the auto-detected value when we asked for detection).
      if (typeof data.effective_skip_rows === "number") {
        setSkipRows(data.effective_skip_rows);
      }
      setHeaderAutoDetected(!!data.header_auto_detected);
      setStep(2);
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      let msg = detail || "Could not parse file";
      if (status === 404) {
        msg = "Import endpoint not found (HTTP 404). Your backend is out of date — re-run deploy/install.sh on the server to rsync the latest code and restart flow-backend.";
      } else if (status === 401 || status === 403) {
        msg = "Not authorised — please sign in again.";
      } else if (status === 413) {
        msg = "File too large. Increase nginx `client_max_body_size` (currently 50M by default) or split the file.";
      } else if (status) {
        msg = `${msg} (HTTP ${status})`;
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const handleFile = async (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (!/\.(csv|txt|xlsx|xlsm|xls)$/i.test(name)) {
      toast.error("Please upload a .csv, .txt or Excel (.xlsx / .xls) file");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File too large — 25 MB max");
      return;
    }
    setFile(f);
    // First parse: pass explicit value only if the user pre-set it > 0 on
    // Step 1 (rare); otherwise let the backend auto-detect the header row.
    await runPreview(f, skipRows > 0 ? skipRows : null);
  };

  // Re-parse when the user changes the skip-rows count on Step 2
  // (explicit override — clears the auto-detect flag).
  const changeSkipRows = async (n) => {
    const clamped = Math.max(0, Math.min(20, Number.isFinite(n) ? n : 0));
    setSkipRows(clamped);
    setHeaderAutoDetected(false);
    if (file) await runPreview(file, clamped);
  };

  const commit = async () => {
    if (!meter) {
      return toast.error("Select which meter this CSV represents (Generation, Import or Export)");
    }
    // Per-meter routing when the project has 2+ meters of the chosen type —
    // matches the backend guard so users see the requirement inline.
    const eligibleMeters = projectMeters.filter(m => m.meter_type === meter);
    if (eligibleMeters.length >= 2 && !meterId) {
      return toast.error(`Select which of the ${eligibleMeters.length} ${meter} meters this CSV is for`);
    }
    const hasTs = !!mapping.timestamp;
    const hasSplitTs = !!mapping.date_col;
    if (!hasTs && !hasSplitTs) {
      return toast.error("Map the Timestamp column, or the Start Date (+ optional Reading Time) columns");
    }
    // Meter-scoped commit only writes the meter's own canonical field.
    // For non-generation meters we ALSO fall back to a generic kWh
    // column mapped as `generation_kwh` (bare-header case).
    const meterCol = mapping[`${meter}_kwh`];
    const bareCol = mapping.generation_kwh;
    if (!meterCol && !(meter !== "generation" && bareCol)) {
      return toast.error(`Map a "${METER_LABEL[meter]} kWh" column (or a generic kWh column) for the ${METER_LABEL[meter]} meter`);
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping_json", JSON.stringify(mapping));
      fd.append("meter", meter);
      if (meterId) fd.append("meter_id", meterId);
      fd.append("skip_rows", String(skipRows));
      fd.append("wipe_range", wipeRange ? "1" : "0");
      const { data } = await api.post(
        `/monitoring/${projectId}/import-csv/commit`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      setStep(3);
      if (data.ok) {
        const wiped = data.wiped_before_insert || 0;
        toast.success(
          `Imported ${data.inserted} ${METER_LABEL[data.meter] || "meter"} samples` +
          (wiped ? ` · ${wiped} wiped first` : ` · ${data.skipped_existing} skipped (already present)`),
        );
      } else {
        toast.error(data.error || "No rows imported");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const finish = () => {
    if (onImported && result?.ok) onImported(result);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="import-csv-dialog">
      <div className="bg-white w-full max-w-3xl border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label flex items-center gap-2">
              <FileSpreadsheet size={11} /> Import · CSV or Excel · half-hourly monitoring
            </div>
            <div className="font-display text-xl font-semibold mt-1">
              {step === 1 && "Upload a CSV or Excel file"}
              {step === 2 && "Map the columns"}
              {step === 3 && (result?.ok ? "Import complete" : "Import finished with issues")}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100"
                  data-testid="csv-close-btn"><X size={16} /></button>
        </div>

        {/* Step strip */}
        <div className="px-5 pt-4 pb-2 flex gap-1 text-[10px] font-mono uppercase tracking-widest">
          {["Upload", "Map columns", "Commit"].map((label, i) => {
            const n = i + 1;
            const active = n === step;
            const done = n < step;
            return (
              <div key={label}
                   className={`px-3 py-1.5 border ${active ? "bg-[var(--ink)] text-white border-[var(--ink)]" : done ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--line)] muted"}`}
                   data-testid={`csv-step-${n}`}>
                {n}. {label}
              </div>
            );
          })}
        </div>

        <div className="p-6 min-h-[360px]">
          {step === 1 && (
            <UploadStep dragOver={dragOver} setDragOver={setDragOver}
                        busy={busy} fileRef={fileRef} onFile={handleFile}
                        skipRows={skipRows} setSkipRows={setSkipRows} />
          )}
          {step === 2 && preview && (
            <MapStep preview={preview} mapping={mapping} setMapping={setMapping}
                     meter={meter}
                     setMeter={(m) => { setMeter(m); setMeterId(""); }}
                     projectMeters={projectMeters}
                     meterId={meterId} setMeterId={setMeterId}
                     skipRows={skipRows} onChangeSkipRows={changeSkipRows}
                     headerAutoDetected={headerAutoDetected}
                     busy={busy}
                     wipeRange={wipeRange} setWipeRange={setWipeRange} />
          )}
          {step === 3 && result && (
            <ResultStep result={result} />
          )}
        </div>

        <div className="flex justify-between items-center gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <div className="text-[11px] muted">
            {step === 1 && "CSV or Excel must include a timestamp column + at least one energy column."}
            {step === 2 && preview && (
              <>Detected <strong>{preview.granularity === "hourly" ? "hourly (60-min)"
                : preview.granularity === "HH" ? "half-hourly (30-min)"
                : preview.granularity === "15min" ? "15-min"
                : "unknown granularity"}</strong> · {preview.row_count} data rows.</>
            )}
            {step === 3 && result?.ok && "CSV rows only fill missing timestamps — existing data untouched."}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <button onClick={onClose} className="btn-ghost" data-testid="csv-cancel-btn">Cancel</button>
            )}
            {step === 2 && (
              <>
                <button onClick={() => { setStep(1); setPreview(null); setFile(null); }}
                        className="btn-ghost" data-testid="csv-back-btn">Back</button>
                <button onClick={commit}
                        disabled={
                          busy || !meter ||
                          (projectMeters.filter(m => m.meter_type === meter).length >= 2 && !meterId)
                        }
                        className="btn-primary"
                        data-testid="csv-commit-btn">
                  {busy ? "Importing…" : meter ? `Import ${METER_LABEL[meter]} rows` : "Import rows"}
                </button>
              </>
            )}
            {step === 3 && (
              <button onClick={finish} className="btn-primary" data-testid="csv-done-btn">
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function UploadStep({ dragOver, setDragOver, busy, fileRef, onFile, skipRows, setSkipRows }) {
  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          const f = e.dataTransfer.files?.[0]; if (f) onFile(f);
        }}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors
                    ${dragOver ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] hover:border-[var(--accent)]"}`}
        data-testid="csv-dropzone">
        <Upload size={28} className="mx-auto mb-3 text-[var(--accent)]" />
        <div className="font-display text-lg font-semibold">
          {busy ? "Parsing file…" : "Drop your CSV or Excel file here, or click to browse"}
        </div>
        <div className="text-xs muted mt-2">
          Half-hourly (30-min), 15-min or hourly energy readings · CSV, TXT or Excel (.xlsx / .xls) · up to 25 MB
        </div>
        <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.xlsm,.xls" className="hidden"
               onChange={(e) => onFile(e.target.files?.[0])}
               data-testid="csv-file-input" />
      </div>

      {/* Skip-preamble-rows control — utility exports often start with a
          title / MPAN metadata row above the actual column headers. Leave
          at 0 to have the backend auto-detect the header row on upload. */}
      <div className="mt-4 flex items-center gap-3 p-4 bg-[var(--surface-2)] border border-[var(--line)]"
           data-testid="csv-skip-rows-row">
        <div className="flex-1">
          <div className="label">Skip preamble rows <span className="ml-1 muted normal-case tracking-normal font-sans">(optional)</span></div>
          <div className="text-[11px] muted mt-1">
            Leave at <span className="font-mono">0</span> — we&apos;ll auto-detect the header row on upload. Only override if your file has an unusual layout the detector missed.
          </div>
        </div>
        <input
          type="number" min="0" max="20" step="1"
          value={skipRows}
          onChange={(e) => setSkipRows(Math.max(0, Math.min(20, parseInt(e.target.value || "0", 10))))}
          className="input-swiss w-20 text-center font-mono"
          data-testid="csv-skip-rows-input" />
      </div>

      <div className="mt-4 bg-[var(--surface-2)] p-4 border border-[var(--line)]">
        <div className="label mb-2">Expected format</div>
        <div className="text-xs muted">
          Any CSV or Excel workbook with a header row on the first sheet · granularity auto-detected (15-min / 30-min / <strong>1-hour</strong>).
          Two supported layouts:
          <ul className="mt-2 list-disc pl-4 space-y-1">
            <li><strong>Combined timestamp</strong> · a single column like <span className="font-mono text-[11px]">Settlement Date Time</span> in ISO or dd/mm/yyyy format.</li>
            <li><strong>Split date + time</strong> · two columns like <span className="font-mono text-[11px]">Start Date</span> + <span className="font-mono text-[11px]">Reading Time</span> (End Date / End Time are also recognised but ignored).</li>
          </ul>
          Energy column headers recognised out of the box:
          <span className="font-mono text-[11px] block mt-1.5">
            Generation (kWh) · Total Consumption (kWh) · Total Self Consumption (kWh) · Export (kWh) · Import (kWh)
          </span>
          <span className="block mt-1.5">
            If your file has a single generic <span className="font-mono">kWh</span> column, map it in the next step to the correct meter type (Generation, Import or Export).
          </span>
        </div>
      </div>
    </div>
  );
}


function MapStep({ preview, mapping, setMapping, meter, setMeter,
                    projectMeters, meterId, setMeterId,
                    skipRows, onChangeSkipRows, headerAutoDetected, busy,
                    wipeRange, setWipeRange }) {
  const options = ["", ...preview.columns];
  const eligibleMeters = React.useMemo(
    () => (projectMeters || []).filter(m => m.meter_type === meter),
    [projectMeters, meter],
  );
  const requiresMeter = eligibleMeters.length >= 2;

  // Auto-pick when there's exactly one candidate — saves a click.
  React.useEffect(() => {
    if (eligibleMeters.length === 1 && meterId !== eligibleMeters[0].id) {
      setMeterId(eligibleMeters[0].id);
    } else if (eligibleMeters.length === 0 && meterId) {
      setMeterId("");
    }
  }, [eligibleMeters.length]);

  // If the CSV has an ambiguous single 'kWh' column (a bare header, not
  // prefixed with Generation/Import/Export), nudge the user to pick which
  // meter type it represents in the field dropdowns below.
  const hasAmbiguousKwh = React.useMemo(() => {
    const energyMapped = ["generation_kwh", "import_kwh", "export_kwh",
                          "consumption_kwh", "self_consumption_kwh"]
      .some((k) => !!mapping[k]);
    if (energyMapped) return false;
    return preview.columns.some((c) => {
      const norm = c.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
      return norm === "kwh" || norm === "energy" || norm === "reading";
    });
  }, [preview.columns, mapping]);

  return (
    <div className="space-y-5">
      {/* Skip-preamble-rows control (also on Step 1) — lets the user
          re-parse the file with a different offset if the header row
          didn't land on line 1 by default. */}
      <div className="flex items-center gap-3 p-3 bg-[var(--surface-2)] border border-[var(--line)]"
           data-testid="csv-skip-rows-row-map">
        <div className="flex-1">
          <div className="label flex items-center gap-2">
            Skip preamble rows
            {headerAutoDetected && skipRows > 0 && (
              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 text-[9px] font-mono uppercase tracking-widest"
                    data-testid="csv-header-auto-detected">
                auto-detected
              </span>
            )}
          </div>
          <div className="text-[11px] muted mt-0.5">
            {headerAutoDetected && skipRows > 0
              ? `We noticed the first ${skipRows} row${skipRows === 1 ? "" : "s"} looked like a title / metadata block and skipped past it. Adjust here if you disagree.`
              : "Change this if the columns below still don\u2019t look right — the file will be re-parsed with the new offset."}
          </div>
        </div>
        <input
          type="number" min="0" max="20" step="1"
          value={skipRows}
          disabled={busy}
          onChange={(e) => onChangeSkipRows(parseInt(e.target.value || "0", 10))}
          className="input-swiss w-20 text-center font-mono"
          data-testid="csv-skip-rows-input-map" />
      </div>

      {/* Meter selector — REQUIRED. One CSV = one meter. */}
      <div data-testid="csv-meter-selector">
        <div className="label mb-2">
          Which meter did this CSV come from? <span className="text-red-500 ml-1">*</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {METERS.map((m) => {
            const active = meter === m.key;
            const Icon = m.icon;
            return (
              <button key={m.key} type="button"
                      onClick={() => setMeter(m.key)}
                      data-testid={`csv-meter-${m.key}`}
                      className={`text-left p-4 border transition-colors ${
                        active
                          ? "border-[var(--accent)] bg-[var(--accent)]/10 ring-1 ring-[var(--accent)]"
                          : "border-[var(--line)] bg-white hover:border-[var(--accent)]"
                      }`}>
                <div className="flex items-center gap-2">
                  <Icon size={16} className={active ? "text-[var(--accent)]" : "text-zinc-500"} />
                  <div className={`font-display font-semibold text-sm ${active ? "text-[var(--accent)]" : ""}`}>
                    {m.label}
                  </div>
                </div>
                <div className="text-[11px] muted mt-1.5">{m.hint}</div>
              </button>
            );
          })}
        </div>
        {!meter && (
          <div className="text-[11px] text-amber-800 mt-2 flex items-center gap-1.5"
               data-testid="csv-meter-warning">
            <AlertCircle size={12} />
            Select a meter before importing — sites typically have three (Generation, Import, Export) and each CSV must be tagged with one.
          </div>
        )}
      </div>

      {/* Meter-instance selector — appears once a meter type is picked AND
          the project has ≥1 registered meter of that type. Required (the
          backend also enforces) when 2+ options exist. */}
      {meter && eligibleMeters.length > 0 && (
        <div data-testid="csv-meter-instance-selector">
          <div className="label mb-2">
            Which {METER_LABEL[meter]?.toLowerCase()} meter?
            {requiresMeter && <span className="text-red-500 ml-1">*</span>}
          </div>
          <select value={meterId} onChange={(e) => setMeterId(e.target.value)}
                  className="input-swiss w-full"
                  data-testid="csv-meter-instance-select">
            {!requiresMeter && <option value="">— use project default —</option>}
            {requiresMeter && <option value="">Select a meter…</option>}
            {eligibleMeters.map(m => (
              <option key={m.id} value={m.id}>
                {m.label}
                {m.install_group ? ` · ${m.install_group}` : ""}
                {m.meter_number ? ` · ${m.meter_number}` : ""}
              </option>
            ))}
          </select>
          {requiresMeter && !meterId && (
            <div className="text-[11px] text-amber-800 mt-1.5 flex items-center gap-1.5">
              <AlertCircle size={12} />
              This project has {eligibleMeters.length} {METER_LABEL[meter]?.toLowerCase()} meters — pick which one this CSV belongs to.
            </div>
          )}
        </div>
      )}

      {hasAmbiguousKwh && (
        <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2"
             data-testid="csv-ambiguous-kwh-nudge">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-mono uppercase tracking-widest text-[10px] mb-1">
              Pick the meter type for the &quot;kWh&quot; column
            </div>
            Your CSV has a single generic energy column. Map it to <strong>Generation kWh</strong>, <strong>Import kWh</strong> or <strong>Export kWh</strong> below — whichever meter this file represents.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} data-testid={`csv-map-${f.key}-row`}>
            <div className="label mb-1.5">
              {f.label}{f.required && <span className="text-red-500 ml-1">*</span>}
            </div>
            <select
              value={mapping[f.key] || ""}
              onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || null }))}
              className="input-swiss w-full"
              data-testid={`csv-map-${f.key}`}>
              {options.map((c) => (
                <option key={c} value={c}>{c || "— not mapped —"}</option>
              ))}
            </select>
            <div className="text-[11px] muted mt-1">{f.hint}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="label mb-2">Preview (first 5 rows)</div>
        <div className="overflow-x-auto border border-[var(--line)]">
          <table className="data-table text-xs">
            <thead>
              <tr>
                {preview.columns.map((c) => {
                  const bound = Object.entries(mapping).find(([, v]) => v === c)?.[0];
                  return (
                    <th key={c} className="whitespace-nowrap">
                      {c}
                      {bound && (
                        <span className="block text-[9px] font-mono lowercase tracking-normal text-[var(--accent)] mt-0.5">
                          → {bound.replace("_kwh", " kWh")}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {preview.sample_rows.map((r, i) => (
                <tr key={i}>
                  {preview.columns.map((c) => (
                    <td key={c} className="font-mono whitespace-nowrap">{r[c]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {preview.detected_range && (
        <div className="text-xs muted">
          Detected time range: <span className="font-mono">{preview.detected_range.start}</span>
          {" → "}
          <span className="font-mono">{preview.detected_range.end}</span>
        </div>
      )}

      {/* Wipe-range toggle — clears THIS meter's field on existing rows in the CSV window */}
      <label className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors
                         ${wipeRange ? "border-red-300 bg-red-50" : "border-[var(--line)] bg-[var(--surface-2)] hover:border-[var(--accent)]"}`}
             data-testid="csv-wipe-row">
        <input type="checkbox" className="mt-0.5" checked={wipeRange}
               onChange={(e) => setWipeRange(e.target.checked)}
               data-testid="csv-wipe-checkbox" />
        <div className="flex-1">
          <div className={`font-mono text-xs uppercase tracking-widest ${wipeRange ? "text-red-800" : ""}`}>
            {meter
              ? `Wipe ${METER_LABEL[meter]} values in range, then insert`
              : "Wipe values in range, then insert"}
          </div>
          <div className={`text-[11px] mt-1 ${wipeRange ? "text-red-700" : "muted"}`}>
            {wipeRange
              ? `⚠ Only this meter's values will be cleared on rows in-range. Other meters (${["generation","import","export"].filter((m)=>m!==meter).map((m)=>METER_LABEL[m]).join(" · ")}) are left intact.`
              : "Default: only fills missing values for the selected meter — existing values for this meter in the range are kept. Tick to overwrite (useful when replacing bad historical data)."}
          </div>
        </div>
      </label>
    </div>
  );
}


function ResultStep({ result }) {
  if (!result.ok) {
    const samples = Array.isArray(result.bad_ts_samples) ? result.bad_ts_samples : [];
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-sm" data-testid="csv-result-fail">
        <AlertCircle size={22} className="text-red-600 mb-3" />
        <div className="font-display text-lg font-semibold text-red-900">Import didn&apos;t write any rows</div>
        <div className="text-sm text-red-800 mt-1">{result.error || "See details below."}</div>
        <ul className="mt-3 text-xs font-mono text-red-800 list-disc pl-4">
          <li>Bad timestamps: {result.skipped_bad_ts}</li>
          <li>Rows with all-null energy values: {result.skipped_all_null}</li>
        </ul>
        {samples.length > 0 && (
          <div className="mt-3 text-xs" data-testid="csv-bad-ts-samples">
            <div className="font-mono uppercase tracking-widest text-[10px] text-red-800 mb-1">
              First unrecognised timestamp values
            </div>
            <ul className="list-disc pl-4">
              {samples.map((s, i) => (
                <li key={i} className="font-mono text-red-900">&quot;{s}&quot;</li>
              ))}
            </ul>
            <div className="mt-2 text-red-800">
              Check your Timestamp / Start Date / Reading Time column mapping in the previous step, or re-save the file with an ISO date format (YYYY-MM-DD).
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="csv-result-ok">
      <CheckCircle2 size={22} className="text-emerald-600 mb-3" />
      <div className="font-display text-2xl font-bold text-emerald-900">
        {result.inserted.toLocaleString()} new {result.meter ? `${result.meter} ` : ""}15-min samples imported
      </div>
      {result.meter && (
        <div className="mt-1.5 text-xs font-mono uppercase tracking-widest text-emerald-800"
             data-testid="csv-result-meter">
          Meter · {result.meter}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
        <Kpi label="Inserted"          value={result.inserted}          tone="ok" />
        <Kpi label="Wiped first"       value={result.wiped_before_insert || 0} tone={result.wiped_before_insert ? "danger" : "muted"} />
        <Kpi label="Skipped (existing)" value={result.skipped_existing} tone="warn" />
        <Kpi label="Bad timestamps"     value={result.skipped_bad_ts}   tone="danger" />
        <Kpi label="Empty rows"         value={result.skipped_all_null} tone="muted" />
      </div>
      <div className="text-xs muted mt-4 font-mono">
        Range: {result.range_start} → {result.range_end}
      </div>
    </div>
  );
}


function Kpi({ label, value, tone }) {
  const tones = {
    ok: "bg-white border-emerald-300 text-emerald-900",
    warn: "bg-white border-amber-300 text-amber-900",
    danger: "bg-white border-red-300 text-red-900",
    muted: "bg-white border-[var(--line)] text-zinc-800",
  };
  return (
    <div className={`p-3 border ${tones[tone] || tones.muted}`}>
      <div className="label">{label}</div>
      <div className="font-display text-xl font-bold mt-1 font-mono">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}
